import { useState } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { buildExportUrl } from '@/helpers/search/url'

import { useHandleError } from '@/hooks/common/useErrorHandler'

import type { SearchRequest } from '@/types/Search'

export const fetchExport = async (
  request: SearchRequest,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const url = buildExportUrl(request)
  const response = await fetch(`${API_URL}/api/v1${url}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch export data.')
  }
  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')

  let name = 'export.csv'
  switch (request.type) {
    case 'dibbs_contract':
      name = 'dibbs_contracts.csv'
      break
    case 'federal_contract':
      name = 'federal_contracts.csv'
      break
    case 'federal_grant':
      name = 'federal_grants.csv'
      break
    case 'sbir':
      name = 'sbir_sttrs.csv'
      break
    case 'sled_contract':
      name = 'sled_contracts.csv'
      break
    default:
      const exhaustiveCheck: never = request.type
      throw new Error(`Unhandled opportunity type: ${exhaustiveCheck}`)
  }

  a.download = name
  a.href = downloadUrl
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export const useSearchExport = () => {
  const { accessToken } = useAuthInfo()
  const { handleError } = useHandleError()

  const [isPending, setIsPending] = useState<boolean>(false)

  const exportSearch = async (request: SearchRequest) => {
    if (accessToken) {
      try {
        setIsPending(true)
        await fetchExport(request, accessToken)
        setIsPending(false)
      } catch (error) {
        setIsPending(false)
        handleError(error)
      }
    }
  }

  return { exportSearch, isPending }
}
